import * as Comlink from 'comlink';
import type { Project } from '@shared/models/project.model';
import type { Recipe } from '@shared/models/recipe.model';
import type { ProductRecord } from '@shared/models/product.model';
import type { EnvironmentHourData } from '@shared/helpers/energy/weather.models';
import type { IDataTable } from '@shared/models/data-table.model';
import type { EnrichScope } from '@shared/helpers/pipeline/enrich-scope.helpers';
import type { ProjectChange } from '@shared/helpers/pipeline/update-engine.models';
import type { ProposalId } from '@shared/models/proposals.model';
import {
  omitBlueprintRastersFromProject,
  stripBlueprintImagesForWorkerTransfer,
  stripBlueprintImagesFromEnrichOptions,
} from '@shared/helpers/design/blueprint-underlay-transfer.helpers';
import { rememberBlueprintRasters } from '@features/design/helpers/blueprint-underlay-raster.cache';
import { resolveDeferredWorkerErrorRejection } from '@client/helpers/worker-error.helpers';
import {
  beginCancellableEnrichment,
  endCancellableEnrichment,
} from './enrich-in-flight.helpers';

export type EnrichProjectOptions = {
  prevProject?: Project;
  productRecord?: ProductRecord;
  recipes?: Recipe[];
  weatherHours?: EnvironmentHourData[];
  dataTables?: IDataTable[];
  scope?: EnrichScope;
  appliedChanges?: ProjectChange[];
  enrichSelectedProposalId?: ProposalId;
};

export type EnrichWorkerAPI = {
  enrich: (project: Project, options?: EnrichProjectOptions) => Project;
  enrichCancellable: (
    project: Project,
    options: EnrichProjectOptions,
    seq: number,
    sequenceBuffer: SharedArrayBuffer,
  ) => Project | null;
};

let worker: Worker | null = null;
let proxy: Comlink.Remote<EnrichWorkerAPI> | null = null;

/** Reject callback for the currently in-flight enrichment (if any). */
let rejectInFlight: ((error: Error) => void) | null = null;

/**
 * Tear down a crashed worker so the next enrichment creates a fresh isolate.
 */
const resetWorker = (): void => {
  worker?.terminate();
  worker = null;
  proxy = null;
};

/**
 * Reject the in-flight enrichment unless Comlink already settled the promise.
 * Comlink rejections and `worker.onerror` can race; defer so the real API error wins.
 */
const rejectInFlightFromWorkerEvent = (
  event: ErrorEvent,
  reject: (error: Error) => void,
): void => {
  queueMicrotask(() => {
    const decision = resolveDeferredWorkerErrorRejection(
      event,
      rejectInFlight === reject,
    );
    // Skip leaves rejectInFlight set so Comlink can still resolve/reject with the real error.
    if (decision.kind === 'skip') return;

    rejectInFlight = null;
    resetWorker();
    reject(decision.error);
    console.error('[enrichOnWorker] worker.onerror:', decision.error.message);
  });
};

const sequenceBuffer =
  typeof SharedArrayBuffer !== 'undefined' ? new SharedArrayBuffer(4) : null;
const sequenceView = sequenceBuffer ? new Int32Array(sequenceBuffer) : null;
let currentSequence = 0;

/**
 * Invalidates any in-flight {@link enrichOnWorkerCancellable} run (worker returns `null` at pipeline checkpoints).
 * No-op when SharedArrayBuffer cancellation is unavailable.
 */
export const bumpEnrichmentCancellationSequence = (): void => {
  if (!sequenceBuffer || !sequenceView) return;
  currentSequence += 1;
  Atomics.store(sequenceView, 0, currentSequence);
};

const getProxy = (): Comlink.Remote<EnrichWorkerAPI> => {
  if (proxy) {
    return proxy;
  }

  worker = new Worker(new URL('./enrich-project.worker.ts', import.meta.url), {
    type: 'module',
  });

  // Catch unhandled worker errors that bypass Comlink (e.g. module load failures).
  // API throws are surfaced by Comlink; empty `onerror` events can race that path.
  worker.onerror = (event) => {
    const reject = rejectInFlight;
    if (!reject) return;
    rejectInFlightFromWorkerEvent(event, reject);
  };

  worker.onmessageerror = () => {
    const reject = rejectInFlight;
    if (!reject) return;

    queueMicrotask(() => {
      if (rejectInFlight !== reject) return;
      rejectInFlight = null;
      resetWorker();
      reject(
        new Error('Worker message could not be deserialized during enrichment'),
      );
      console.error('[enrichOnWorker] worker.onmessageerror');
    });
  };

  proxy = Comlink.wrap<EnrichWorkerAPI>(worker);
  return proxy;
};

/**
 * Prepare a project for worker postMessage: drop large blueprint rasters that
 * enrichment never reads (they can OOM / crash the worker isolate).
 */
const prepareProjectForEnrichWorker = (
  project: Project,
  options: EnrichProjectOptions,
): {
  project: Project;
  options: EnrichProjectOptions;
  imagesById: Record<string, string>;
} => {
  const stripped = stripBlueprintImagesForWorkerTransfer(project);
  const imagesById = { ...stripped.imagesById };
  const nextOptions = stripBlueprintImagesFromEnrichOptions(
    options,
    imagesById,
  );
  return { project: stripped.project, options: nextOptions, imagesById };
};

/**
 * Keep rasters in the client cache and off the enriched project document.
 */
const finishEnrichWithCachedRasters = (
  result: Project,
  imagesById: Record<string, string>,
): Project => {
  rememberBlueprintRasters(imagesById);
  return omitBlueprintRastersFromProject(result);
};

/**
 * Enrich a project on a web worker (non-cancellable).
 * Use for one-off enrichments (exports, etc.) that must run to completion.
 */
export const enrichOnWorker = (
  project: Project,
  options: EnrichProjectOptions = {},
): Promise<Project> => {
  const prepared = prepareProjectForEnrichWorker(project, options);
  const comlinkPromise = getProxy().enrich(prepared.project, prepared.options);

  // Race the Comlink call against a manually-rejectable promise so that
  // worker-level errors (onerror) can surface instead of hanging forever.
  return new Promise<Project>((resolve, reject) => {
    rejectInFlight = reject;
    comlinkPromise.then(
      (result) => {
        // A newer enrich may already own rejectInFlight.
        if (rejectInFlight === reject) rejectInFlight = null;
        resolve(finishEnrichWithCachedRasters(result, prepared.imagesById));
      },
      (error) => {
        if (rejectInFlight === reject) rejectInFlight = null;
        reject(error);
      },
    );
  });
};

/**
 * Enrich with cooperative cancellation. Advances the sequence counter
 * so any in-flight worker run sees itself as stale via {@link SharedArrayBuffer}.
 * Falls back to non-cancellable enrichment when SAB is unavailable.
 */
export const enrichOnWorkerCancellable = (
  project: Project,
  options: EnrichProjectOptions,
): Promise<Project | null> => {
  if (!sequenceBuffer || !sequenceView) {
    return enrichOnWorker(project, options);
  }
  const prepared = prepareProjectForEnrichWorker(project, options);
  const seq = ++currentSequence;
  Atomics.store(sequenceView, 0, seq);
  beginCancellableEnrichment();
  const comlinkPromise = getProxy().enrichCancellable(
    prepared.project,
    prepared.options,
    seq,
    sequenceBuffer,
  );

  return new Promise<Project | null>((resolve, reject) => {
    rejectInFlight = reject;
    comlinkPromise.then(
      (result) => {
        // A newer enrich may already own rejectInFlight.
        if (rejectInFlight === reject) rejectInFlight = null;
        resolve(
          result
            ? finishEnrichWithCachedRasters(result, prepared.imagesById)
            : result,
        );
      },
      (error) => {
        if (rejectInFlight === reject) rejectInFlight = null;
        reject(error);
      },
    );
  }).finally(endCancellableEnrichment);
};
